import { chain, get, capitalize, includes } from "lodash"
import moment from "moment"
import { dollarFormatter } from "helpers/formatters/numbers"
import { Creators as ExperimentCreators } from "modules/experiments/model"
import { Creators as SegmentsCreator } from "modules/compare-segments-root/model"
import { getSegmentNormalization, NORMALIZATION } from "helpers/segments"

const EDITABLE_TYPES = ["OPTIMIZATION", "PORTFOLIO", "SIMULATION"]

export function getItemInfo(item) {
  let values = []
  switch (item.type) {
    case "experiment":
      values = [{ label: "Description", value: item.description }]
      if (item.experimentType === "SMART_TRADE") {
        values.push({ label: "Trade Size", value: dollarFormatter(item.tradeSize) },
          { label: "Risk Model", value: get(item, "model.name", "") })
      }
      if (item.lastUpdated) {
        values.push({ label: "Last Updated", value: moment(item.lastUpdated).format("YYYY-MM-DD") })
      }
      break
    default:
      break
  }
  return [{
    values,
  }]
}

export function getItemActions(item, nbItems, longShortData) {
  const isContributor = getSegmentNormalization(item) === NORMALIZATION.CONTRIBUTION
  const experimentType = get(item, "experimentType")

  const actions = [{
    condition: !item.initialItem,
    action: {
      group: 1,
      label: "Remove",
      fn: () => SegmentsCreator.removeComparisonItem(item, "experiments-remove-comparison"),
    },
  }, {
    condition: !item.initialItem && !item.segment,
    action: {
      group: 1,
      label: "View Trades",
      disabled: item.type !== "experiment" || item.noData || item.isUnmodeled || item.uploading,
      fn: () => ExperimentCreators.toggleTradesPanel(item),
    },
  }, {
    condition: item.type === "experiment" && includes(["OPTIMIZATION", "SIMULATION"], experimentType),
    action: {
      group: 1,
      label: "Create Portfolio",
      disabled: item.noData || item.isUnmodeled,
      fn: () => ExperimentCreators.toggleCreatePortfolio(item),
    },
  }, {
    condition: !item.initialItem && !item.segment && includes(["security", "swap"], item.type),
    action: {
      group: 1,
      label: `Evaluate As ${item.evaluateAs === "short" ? "Long" : "Short"}`,
      disabled: item.noData || item.isUnmodeled,
      fn: () => ExperimentCreators.evaluateAs(item),
    },
  }, {
    condition: !item.initialItem && !item.segment && experimentType,
    action: {
      group: 2,
      label: "Rename",
      disabled: !includes(EDITABLE_TYPES, experimentType),
      fn: () => ExperimentCreators.toggleRenameExperiment(item),
    },
  }, {
    condition: !item.initialItem && !item.segment && experimentType,
    action: {
      group: 2,
      label: experimentType === "PORTFOLIO" ? "Upload" : `Update ${capitalize(experimentType)}`,
      disabled: item.isUnmodeled || !includes(EDITABLE_TYPES, experimentType),
      fn: () => ExperimentCreators.toggleUpdateExperiment(item),
    },
  }, {
    condition: !item.initialItem && !item.segment && experimentType,
    action: {
      group: 2,
      label: "Delete",
      disabled: item.type !== "experiment",
      fn: () => ExperimentCreators.deleteExperiment(item),
      confirmationDialog: {
        title: `Delete Experiment: ${item.label}`,
        content: `Are you sure you want to delete ${item.label}? All associated position data will be deleted.`,
        buttonText: "Delete",
      },
    },
  }, {
    condition: longShortData && !item.segment,
    action: {
      group: 3,
      label: "Split into LONG/SHORT",
      fn: () => SegmentsCreator.splitItem(item, "experiments-split-item"),
      disabled: nbItems > 3,
      disabledTooltip: "There is not enough space to add the 2 segments",
    },
  }, {
    condition: item.segment,
    action: {
      group: 3,
      label: `Normalize As ${isContributor ? "Standalone" : "Contributor"}`,
      fn: () => SegmentsCreator.setSegmentNormalization(item, isContributor ? NORMALIZATION.STANDALONE : NORMALIZATION.CONTRIBUTION, "experiments-set-segment-normalization"),
    },
  }, {
    condition: !item.initialItem,
    action: {
      group: 2,
      label: `${item.fxHedgeEnabled ? "Disable" : "Enable"} FX Hedge`,
      fn: () => ExperimentCreators.toggleFxHedge(item),
    },
  }]
  return chain(actions)
    .filter(({ condition }) => condition)
    .map(({ action }) => action)
    .value()
}

export function getEditExperimentUrl(accountAlias, experiment) {
  const { id, portfolioId, experimentType } = experiment
  return `/${accountAlias}/p/${portfolioId}/experiments/${id}/${experimentType === "PORTFOLIO" ? "upload" : "edit"}`
}
